"use client"

import type { CareerCenter } from "@/modules/careerCenter/types"
import type { I18nLocale } from "@/modules/i18n/types"
import type { JobAd } from "@/modules/jobAds/client/api/types"
import type { ComponentProps, FunctionComponent } from "react"

import { useEffect, useRef } from "react"

import { isArrayFilled } from "@/modules/feedbox/utils/isArrayFilled"
import { JOB_ADS_CANDIDACY_TYPES } from "@/modules/jobAds/client/api/constants"
import { JobAdCard } from "@/modules/jobAds/client/components/JobAdCard/JobAdCard"
import { PUBLICATION_TYPES } from "@/modules/jobAds/client/constants"
import { useJobAdsClientTracking } from "@/modules/jobAds/client/hooks/useJobAdsClientTracking"
import { getDisplayableLocation } from "@/modules/locations/locations"

type ResultCardProps = {
  alumniBadgeLabel: string
  canBeLiked: boolean
  careerCenterLogo: CareerCenter["logo"]
  careerCenterName: CareerCenter["name"]
  contractDescription: string
  easyApplyLabel: string
  isCareerCenter: boolean
  isLiked: boolean
  isPostedByPartnerCompany: boolean
  isSeen: boolean
  jobAd: JobAd
  locale: I18nLocale
  multipleLocationsLabel: string
  orientation?: ComponentProps<typeof JobAdCard>["orientation"]
  partnerBadgeLabel: string
  saveLabel: string
  sponsoredJobAdLabel: string
  targetBadgeLabel: string
  timeZone: Intl.ResolvedDateTimeFormatOptions["timeZone"]
  unSaveLabel: string
}

export const ResultCard: FunctionComponent<ResultCardProps> = ({
  alumniBadgeLabel,
  canBeLiked,
  careerCenterLogo,
  careerCenterName,
  contractDescription,
  easyApplyLabel,
  isCareerCenter,
  isLiked,
  isPostedByPartnerCompany,
  isSeen,
  jobAd,
  locale,
  multipleLocationsLabel,
  orientation,
  partnerBadgeLabel,
  saveLabel,
  sponsoredJobAdLabel,
  targetBadgeLabel,
  timeZone,
  unSaveLabel,
}) => {
  const ref = useRef(null)
  const { setRefererParams, trackJobAdClick, trackJobAdDisplayNext } = useJobAdsClientTracking()

  const {
    activationTime,
    candidacyType,
    company,
    company: { logoUrl: companyLogo, name: companyName },
    createdByStudentOrAlumni,
    exclusive: isExclusive,
    id,
    location,
    locations,
    position,
    premium,
    privateOffer: isPrivate,
    publicationType,
    searchIndex,
    slug,
    title,
  } = jobAd

  const isPremium = publicationType ? publicationType.score === PUBLICATION_TYPES.FLASH_OR_SPONSORED : Boolean(premium)
  const hasPartnerSignature = isCareerCenter && isPostedByPartnerCompany
  const hasAlumniSignature = isCareerCenter && createdByStudentOrAlumni
  const hasTargetedJobSignature = isCareerCenter && isExclusive
  const isEasyApply = candidacyType === JOB_ADS_CANDIDACY_TYPES.INTERNAL
  const url = `/${locale}/job-offers/${slug}`

  useEffect(() => {
    const onObserve: IntersectionObserverCallback = entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          trackJobAdDisplayNext({
            company_id: company.id,
            index: searchIndex,
            job_ad_id: id,
            position,
            sponsored_job_ads: premium,
          })

          observer.unobserve(entry.target)
        }
      }
    }

    let observedValue: Element | null = null
    const observer = new IntersectionObserver(onObserve, { threshold: 0.8 })

    if (ref.current) {
      observer.observe(ref.current)
      observedValue = ref.current
    }

    return () => {
      if (observedValue) {
        observer.unobserve(observedValue)
      }
    }
  }, [premium, searchIndex, position, id, company.id, trackJobAdDisplayNext])

  return (
    <li key={id} ref={ref}>
      <JobAdCard
        alumniBadgeLabel={alumniBadgeLabel}
        companyLogoUrl={companyLogo}
        companyName={companyName}
        contractDescription={contractDescription}
        dataTestid="jobad-card"
        displaySaveButton={canBeLiked}
        easyApplyLabel={easyApplyLabel}
        id={id}
        internalJobAd={isEasyApply}
        isAlumniJobad={hasAlumniSignature}
        isJobAdSaved={isLiked}
        isPartnerJobAd={hasPartnerSignature}
        isSeen={isSeen}
        isSponsoredJobAd={isPremium}
        isTargetedJobAd={hasTargetedJobSignature}
        locale={locale}
        locations={(isArrayFilled(locations, 2) ? multipleLocationsLabel : getDisplayableLocation(location)) || ""}
        onClick={() => {
          const params = {
            id,
            index: searchIndex,
            position,
          }

          setRefererParams(params, { keyName: url })
          trackJobAdClick({
            easy_to_apply: isEasyApply,
            index: searchIndex,
            is_public: !isPrivate,
            job_ad_id: id,
            position,
          })
        }}
        orientation={orientation}
        partnerBadgeLabel={partnerBadgeLabel}
        postedDate={activationTime.toString()}
        saveLabel={saveLabel}
        schoolLogo={careerCenterLogo}
        schoolName={careerCenterName || ""}
        sponsoredJobAdLabel={sponsoredJobAdLabel}
        targetBadgeLabel={targetBadgeLabel}
        timeZone={timeZone}
        title={title}
        trackEventModule="fo_job_search"
        trackingContext="job_search_results"
        unSaveLabel={unSaveLabel}
        url={url}
      />
    </li>
  )
}
